import { ArrowLeftOutlined, CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, DownloadOutlined, ExclamationCircleOutlined, InfoCircleOutlined, OrderedListOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Divider, Progress, Row, Select, Spin, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, } from "react-virtualized";
import { TreeSelectLocationUser } from "../../components/TreeSelectLocation";
import { TreeSelectTopic } from "../../components/TreeSelectTopic";
import Api, { Api_v2, exceptionNotificationAPI } from "../../services/api";
import { TASK_STATUS } from "../FormBuilder/const";

const { Title, Text } = Typography;
const { Option } = Select;

const CORRELATION_STATUS = {
    0: "Idle",
    1: "Executing",
    2: "Finished",
    3: "Faulted",
    4: "Aborted",
};

const WorkflowTab = () => {
    const { t } = useTranslation();

    const [workflows, setWorkflows] = useState(null);
    const [definitionId, setDefinitionId] = useState(null);
    const [correlations, setCorrelations] = useState(null);
    const [correlationId, setCorrelationId] = useState(null);
    const [tasks, setTasks] = useState(null);
    const [loading, setLoading] = useState(false);

    const [locationId, setLocationId] = useState(null);
    const [correlationStatusFilter, setCorrelationStatusFilter] = useState(null);
    const [dateRangeFilter, setDateRangeFilter] = useState(null);

    const [workflowListLocationId, setWorkflowListLocationId] = useState(null);
    const [workflowListTopicId, setWorkflowListTopicId] = useState(null);
    const [workflowListDateRangeFilter, setWorkflowListDateRangeFilter] = useState(null);

    const workflowCache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 100,
    });
    const correlationCache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 100,
    });

    const [statusCounts, setStatusCounts] = useState({
        done: 0,
        late: 0,
        open: 0,
        pending: 0,
    });

    const [corrStatusCounts, setCorrStatusCounts] = useState({
        idle: 0,
        executing: 0,
        finished: 0,
        faulted: 0,
        aborted: 0,
    });

    useEffect(() => {
        fetchWorkflows();
    }, [workflowListLocationId, workflowListTopicId, workflowListDateRangeFilter]);

    useEffect(() => {
        // Resetar filtro de data quando locationId ou definitionId mudarem
        setDateRangeFilter(null);
        setCorrelationStatusFilter(null);
    }, [locationId, definitionId]);

    // Busca automática com debounce de 400ms quando os filtros mudarem
    useEffect(() => {
        if (!definitionId) return;
        
        const timer = setTimeout(() => {
            fetchCorrelations(definitionId, locationId);
        }, 400);
        
        return () => clearTimeout(timer);
    }, [locationId, correlationStatusFilter, dateRangeFilter, definitionId]);

    useEffect(() => {
        if (tasks && tasks.length > 0) {
            const done = tasks.filter((t) => t.status === "Done").length;
            const late = tasks.filter((t) => t.status === "Late").length;
            const open = tasks.filter((t) => t.status === "Open").length;
            const pending = tasks.filter((t) => t.status === "Pending").length;
            setStatusCounts({ done, late, open, pending });
        } else {
            setStatusCounts({ done: 0, late: 0, open: 0, pending: 0 });
        }
    }, [tasks]);

    useEffect(() => {
        if (correlations && correlations.length > 0) {
            const idle = correlations.filter((c) => c.status === 0).length;
            const executing = correlations.filter((c) => c.status === 1).length;
            const finished = correlations.filter((c) => c.status === 2).length;
            const faulted = correlations.filter((c) => c.status === 3).length;
            const aborted = correlations.filter((c) => c.status === 4).length;
            setCorrStatusCounts({ idle, executing, finished, faulted, aborted });
        } else {
            setCorrStatusCounts({
                idle: 0,
                executing: 0,
                finished: 0,
                faulted: 0,
                aborted: 0,
            });
        }
    }, [correlations]);

    const fetchWorkflows = async () => {
        setLoading(true);
        try {
            const locId = workflowListLocationId != null && workflowListLocationId !== ""
                ? (Array.isArray(workflowListLocationId) ? workflowListLocationId[0] : workflowListLocationId)
                : undefined;
            const topicId = workflowListTopicId != null && workflowListTopicId !== ""
                ? (Array.isArray(workflowListTopicId) ? workflowListTopicId[0] : workflowListTopicId)
                : undefined;
            const request = {
                locationId: locId,
                topicId: topicId,
                locationIncludeChildren: true,
                locationDirectParents: false,
                onlyInstances:true
            };
            if (workflowListDateRangeFilter && Array.isArray(workflowListDateRangeFilter) && workflowListDateRangeFilter.length === 2) {
                const [startDate, endDate] = workflowListDateRangeFilter;
                if (startDate && endDate) {
                    request.startDate = startDate.format('YYYY-MM-DD');
                    request.endDate = endDate.format('YYYY-MM-DD');
                }
            }
            const response = await Api_v2.Workflow.DefinitionGetAll(request);
            const raw = response.data && Array.isArray(response.data) ? response.data : [];
            const mapped = raw
                .filter((r) => r.latestPublishedVersionPublished)
                .map((r) => ({
                    definitionId: r.definitionId,
                    name: r.name,
                    version: r.latestPublishedVersion ?? r.version,
                    executingCount: r.executingCount ?? 0,
                    finishedCount: r.finishedCount ?? 0,
                    faultedCount: r.faultedCount ?? 0,
                    abortedCount: r.abortedCount ?? 0,
                }));
            setWorkflows(mapped);
            resetState();
        } catch (error) {
            exceptionNotificationAPI(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCorrelations = async (definitionId, locId = null, includeChildren = true, overrideStatusFilter = null) => {
        setLoading(true);
        try {
            const locationIdParam = locId != null && locId !== "" ? String(locId) : "";
            const endpoint =
                "Task/getLatestPublishedWorkflowsInstances/" +
                definitionId +
                (locationIdParam ? "/" + encodeURIComponent(locationIdParam) : "");
            
            // Construir parâmetros de query
            const params = {};
            if (locationIdParam) {
                params.locationIncludeChildren = includeChildren;
            }
            
            // Adicionar parâmetros de data se fornecidos e válidos
            if (dateRangeFilter && Array.isArray(dateRangeFilter) && dateRangeFilter.length === 2) {
                const startDate = dateRangeFilter[0];
                const endDate = dateRangeFilter[1];
                
                if (startDate && endDate) {
                    // Converter dayjs para moment para manter compatibilidade com a API
                    let startMoment, endMoment;
                    
                    // Verificar se é dayjs (objetos dayjs têm método format e isValid, e não são moment)
                    const isDayjs = (date) => {
                        return date && 
                               typeof date.format === 'function' && 
                               typeof date.isValid === 'function' && 
                               !moment.isMoment(date) &&
                               date.$L !== undefined; // dayjs tem propriedade interna $L
                    };
                    
                    // Se for dayjs, converter para moment
                    if (isDayjs(startDate)) {
                        startMoment = moment(startDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
                    } 
                    // Se já for moment, usar diretamente
                    else if (moment.isMoment(startDate)) {
                        startMoment = startDate;
                    }
                    // Se tiver método format (pode ser dayjs sem $L), tentar converter
                    else if (startDate && typeof startDate.format === 'function') {
                        startMoment = moment(startDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
                    }
                    // Caso contrário, tentar criar um moment normalmente
                    else {
                        startMoment = moment(startDate);
                    }
                    
                    // Mesmo processo para endDate
                    if (isDayjs(endDate)) {
                        endMoment = moment(endDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
                    }
                    else if (moment.isMoment(endDate)) {
                        endMoment = endDate;
                    }
                    else if (endDate && typeof endDate.format === 'function') {
                        endMoment = moment(endDate.format('YYYY-MM-DD'), 'YYYY-MM-DD', true);
                    }
                    else {
                        endMoment = moment(endDate);
                    }
                    
                    // Validar que ambas as datas são válidas antes de adicionar aos parâmetros
                    if (startMoment.isValid() && endMoment.isValid()) {
                        // Garantir que startDate não seja maior que endDate
                        if (startMoment.isBefore(endMoment) || startMoment.isSame(endMoment, 'day')) {
                            params.startDate = startMoment.format('YYYY-MM-DD');
                            params.endDate = endMoment.format('YYYY-MM-DD');
                        }
                    }
                }
            }
            
            // Adicionar parâmetro de status se fornecido (usar override se fornecido, senão usar o estado)
            const statusFilterToUse = overrideStatusFilter !== null ? overrideStatusFilter : correlationStatusFilter;
            if (statusFilterToUse != null && statusFilterToUse !== '') {
                params.status = parseInt(statusFilterToUse, 10);
            }
            
            const response = await Api.post(endpoint, null, { params });
            setCorrelations(response.data.response);
            setDefinitionId(definitionId);
            setTasks(null); // Reseta as tarefas
            correlationCache.clearAll();
        } catch (error) {
            exceptionNotificationAPI(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchTasks = async (corrId) => {
        setLoading(true);
        setCorrelationId(null);
        try {
            const response = await Api.post(`Task/getTasksByCorrelationId/${corrId}`);
            setCorrelationId(corrId);
            setTasks(response.data.response);
        } catch (error) {
            exceptionNotificationAPI(error);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (corrId) => {
        setLoading(true);
        try {
            const response = await Api_v2.Workflow.PdfReport(corrId);
            const url = window.URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );
            const date = new Date().toISOString().split("T")[0].replace(/-/g, "");
            const filename = `Workflow_${corrId}_Report_${date}.pdf`;
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Erro ao baixar o PDF", error);
        } finally {
            setLoading(false);
        }
    };

    const resetState = () => {
        setDefinitionId(null);
        setCorrelations(null);
        setTasks(null);
    };

    const goBack = () => {
        if (tasks) {
            setTasks(null);
        } else if (correlations) {
            setCorrelations(null);
            setDefinitionId(null);
        }
    };


    // Filtragem agora é feita no backend, apenas retornar correlations diretamente
    const filteredCorrelations = useMemo(() => {
        if (!correlations) return [];
        return correlations;
    }, [correlations]);

    const getCorrelationStatusTag = (status) => {
        var color = "";
        switch (status) {
            case 0:
                color = "rgb(199, 200, 202)"; // Idle
                break;
            case 1:
                color = "#4085c6"; // Executing
                break;
            case 2:
                color = "#34d399"; // Finished
                break;
            case 3:
            case 4:
                color = "#ff4d4f"; // Faulted / Aborted
                break;
            default:
                color = "default";
        }

        return <div
            style={{
                backgroundColor: color,
                color: '#fff',
                fontWeight: 500,
                borderRadius: 8,
                padding: '2px 8px',
                fontSize: 12,
                display: 'inline-block',
                marginRight: 8,
            }}
        >
            {t(CORRELATION_STATUS[status])}
        </div>
    };

    const getTaskStatusTag = (status) => {
        const originalStatus = status;
        status = TASK_STATUS[status];
        let color = "default";
        if (status === TASK_STATUS.Pending) {
            color = "rgb(199, 200, 202)";
        } else if (status === TASK_STATUS.Open) {
            color = "#4085c6";
        } else if (status === TASK_STATUS.Done) {
            color = "#34d399";
        } else if (status === TASK_STATUS.Late) {
            color = "#ff4d4f";
        }
        return <Tag color={color}>{t(originalStatus)}</Tag>;
    };

    const groupTasksByParent = (tasksList) => {
        return tasksList.reduce((acc, task) => {
            if (task.taskTypeId === "df3cb0ad-9e0a-44e1-898c-3e3a5df1483e") {
                if (!acc[task.taskId]) {
                    acc[task.taskId] = { parent: null, approvals: [] };
                }
                acc[task.taskId].approvals.push(task);
            } else {
                acc[task.id] = acc[task.id] || { parent: null, approvals: [] };
                acc[task.id].parent = task;
            }
            return acc;
        }, {});
    };

    const getCorrelationPercent = (count) => {
        if (!correlations || correlations.length === 0) return 0;
        return Math.round((count / correlations.length) * 100);
    };

    const getPercent = (count) => {
        if (!tasks || tasks.length === 0) return 0;
        return Math.round((count / tasks.length) * 100);
    };

    const renderStatusBadge = (count, label, color) => {
        if (!count || count === 0) return null;
        return (
            <div
                style={{
                    backgroundColor: color,
                    color: '#fff',
                    borderRadius: 8,
                    padding: '4px 12px',
                    fontSize: 12,
                    fontWeight: 500,
                    display: 'inline-block',
                }}
            >
                {count} {label}
            </div>
        );
    };

    const renderWorkflowRow = ({ index, key, style, parent }) => {
        const workflow = workflows[index];
        return (
            <CellMeasurer
                key={key}
                cache={workflowCache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ registerChild }) => (
                    <div style={style} ref={registerChild}>
                        <Card
                            className="new-card-history card-p-10"
                            style={{ cursor: "pointer", marginBottom: "5px" }}
                            onClick={() => {
                                workflowCache.clearAll();
                                fetchCorrelations(workflow.definitionId, locationId);
                            }}
                        >
                            <Row>
                                <Col span={8}>
                                    <Text>{t("Workflow Name")}: </Text>
                                    <br />
                                    <strong style={{ color: "#000" }}>{workflow.name}</strong>
                                </Col>
                                <Col span={8}>
                                    <Text>{t("Version")}: </Text>
                                    <br />
                                    <strong style={{ color: "#000" }}>{workflow.version}</strong>
                                </Col>
                                <Col span={8} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                                    {renderStatusBadge(workflow.executingCount, t('Executing'), '#4085c6')}
                                    {renderStatusBadge(
                                        (workflow.faultedCount || 0) + (workflow.abortedCount || 0),
                                        t('Error'),
                                        'rgb(199, 200, 202)'
                                    )}
                                    {renderStatusBadge(workflow.finishedCount, t('Finished'), '#34d399')}
                                </Col>
                            </Row>
                        </Card>
                    </div>
                )}
            </CellMeasurer>
        );
    };

    const renderCorrelationRow = ({ index, key, style, parent }) => {
        const correlation = filteredCorrelations[index];
        const statusColor = getStatusColor(correlation.status);
        return (
            <CellMeasurer
                key={key}
                cache={correlationCache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                {({ registerChild }) => (
                    <div style={style} ref={registerChild}>
                        <Card
                            bodyStyle={{ padding: 0 }}
                            style={{
                                overflow: 'hidden',
                                marginBottom: 8,
                                cursor: 'pointer',
                                borderLeft: `4px solid ${statusColor}`
                            }}
                            onClick={() => {
                                correlationCache.clearAll();
                                fetchTasks(correlation.correlationId);
                            }}
                        >
                            <div style={{ background: '#fff', padding: 12, borderLeft: `4px solid ${statusColor}` }}>
                                <Row justify="space-between" align="middle">
                                    <Col>
                                        {getCorrelationStatusTag(correlation.status)}
                                        <Text strong style={{ fontSize: 16 }}>
                                            Workflow: {correlation.correlationId}
                                        </Text>
                                    </Col>
                                </Row>
                                {/* Details */}
                                <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                    <Col span={8}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('LastTaskDescription')}:</Text>
                                        <br />
                                        <Text strong>{correlation.lastRunningTaskName}</Text>
                                    </Col>
                                    <Col span={4}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskLocationName')}:</Text>
                                        <br />
                                        <Text strong>{correlation.lastRunningLocationName}</Text>
                                    </Col>
                                    <Col span={4}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('LastFormName')}:</Text>
                                        <br />
                                        <Text strong>{correlation.lastRunningFormName}</Text>
                                    </Col>
                                    <Col span={4}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{t('LastTaskAssignedTo')}:</Text>
                                        <br />
                                        <Text strong>{correlation.lastRunningTaskUserName}</Text>
                                    </Col>
                                </Row>
                            </div>
                        </Card>
                    </div>
                )}
            </CellMeasurer>
        );
    };

    // Helper to map task status to a border color
    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending':
                return 'rgb(199, 200, 202)';
            case 'Open':
                return '#4085c6';
            case 'Late':
                return '#ff4d4f';
            case 'Done':
                return '#34d399';
            case 'Approval':
                return '#60a5fa';
            case 0:
                return "rgb(199, 200, 202)"; // Idle
            case 1:
                return "#4085c6"; // Executing
            case 2:
                return "#34d399"; // Finished
            case 3:
            case 4:
                return "#ff4d4f"; // Faulted / Aborted
            default:
                return '#888';
        }
    };

    return (
        <Spin spinning={loading} size="large">
            <div>
                {correlations && !tasks && (
                    <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
                        <Col style={{ minWidth: 125 }}>
                            <Button
                                type="primary"
                                onClick={goBack}
                                icon={<ArrowLeftOutlined />}
                            >
                                {t("Workflow.BackToWorkflows")}
                            </Button>
                        </Col>
                        <Col style={{ minWidth: 125 }}>
                            <TreeSelectLocationUser
                                placeholder={t("Location")}
                                includeDirectParents={false}
                                className="space-search"
                                value={locationId != null && locationId !== "" ? locationId : undefined}
                                onChange={(e) => setLocationId(Array.isArray(e) ? (e[0] ?? null) : e ?? null)}
                            />
                        </Col>
                        <Col>
                            <Select
                                allowClear
                                placeholder={t("Status")}
                                value={correlationStatusFilter}
                                onChange={(value) => setCorrelationStatusFilter(value)}
                            >
                                <Option value="0">{t("Idle")}</Option>
                                <Option value="1">{t("Executing")}</Option>
                                <Option value="2">{t("Finished")}</Option>
                                <Option value="3">{t("Faulted")}</Option>
                                <Option value="4">{t("Aborted")}</Option>
                            </Select>
                        </Col>
                        <Col>
                            <DatePicker.RangePicker
                                value={dateRangeFilter && dateRangeFilter.length === 2 ? dateRangeFilter : null}
                                placeholder={[t("Reports.StartDate"), t("Reports.EndDate")]}
                                format="YYYY-MM-DD"
                                onChange={(dates) => {
                                    if (dates && dates.length === 2 && dates[0] && dates[1]) {
                                        setDateRangeFilter(dates);
                                    } else {
                                        setDateRangeFilter(null);
                                    }
                                }}
                                allowClear
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                )}

                {correlations && !tasks && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 10 }}>
                        {[
                            { key: 'executing', title: t('Executing'), count: corrStatusCounts.executing, progress: getCorrelationPercent(corrStatusCounts.executing), icon: InfoCircleOutlined, color: '#4085c6', statusValue: '1' },
                            { key: 'finished', title: t('Finished'), count: corrStatusCounts.finished, progress: getCorrelationPercent(corrStatusCounts.finished), icon: CheckCircleOutlined, color: '#67D181', statusValue: '2' },
                            { key: 'faulted', title: t('Faulted'), count: corrStatusCounts.faulted, progress: getCorrelationPercent(corrStatusCounts.faulted), icon: ExclamationCircleOutlined, color: '#ff4d4f', statusValue: '3' },
                            { key: 'aborted', title: t('Aborted'), count: corrStatusCounts.aborted, progress: getCorrelationPercent(corrStatusCounts.aborted), icon: CloseCircleOutlined, color: '#ff4d4f', statusValue: '4' },
                            { key: 'total', title: t('Total'), count: corrStatusCounts.idle + corrStatusCounts.executing + corrStatusCounts.finished + corrStatusCounts.faulted + corrStatusCounts.aborted, progress: 100, icon: OrderedListOutlined, color: '#999999', statusValue: null }
                        ].map((action, idx) => {
                            const Icon = action.icon;
                            const handleCardClick = () => {
                                // Atualizar o filtro de status no estado
                                // O debounce automático vai disparar a busca após 400ms
                                setCorrelationStatusFilter(action.statusValue);
                            };
                            return (
                                <Col xs={24} sm={12} md={4} key={idx} style={{ height: '100%' }}>
                                    <Tooltip title={action.statusValue !== null ? `${t('BtnFilter')} ${action.title}` : ''}>
                                        <Card
                                            hoverable={action.statusValue !== null}
                                            onClick={action.statusValue !== null ? handleCardClick : undefined}
                                            style={{
                                                height: '100%',
                                                minHeight: 100,
                                                width: "100%",
                                                alignItems: 'center',
                                                cursor: action.statusValue !== null ? 'pointer' : 'default',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                                <Icon style={{ fontSize: 28, color: action.color, marginRight: 12 }} />
                                                <div style={{ flex: 1, width: "100%" }}>
                                                    <Text
                                                        style={{
                                                            marginBottom: 4,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {action.title}
                                                    </Text>
                                                    <Title
                                                        level={4}
                                                        style={{
                                                            marginTop: 0,
                                                            marginBottom: 4,
                                                            whiteSpace: 'normal',
                                                            wordBreak: 'break-word',
                                                        }}
                                                    >
                                                        {action.count}
                                                    </Title>
                                                    <Progress percent={action.progress} strokeColor={action.color} />
                                                </div>
                                            </div>
                                        </Card>
                                    </Tooltip>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {tasks && (
                    <Row gutter={[8, 8]} style={{ marginBottom: "10px" }}>
                        <Col>
                            <Button
                                type="primary"
                                onClick={goBack}
                                icon={<ArrowLeftOutlined />}
                            >
                                {t("Workflow.BackToCorrelations")}
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="default"
                                onClick={() => handleDownload(correlationId)}
                                icon={<DownloadOutlined />}
                            >
                                {t("Report")}
                            </Button>
                        </Col>
                    </Row>
                )}

                {tasks && tasks.length > 0 && (
                    <Row gutter={[16, 16]} style={{ marginBottom: 10 }}>
                        {[
                            { key: 'done', title: t('Done'), count: statusCounts.done, progress: getPercent(statusCounts.done), icon: CheckCircleOutlined, color: '#67D181' },
                            { key: 'late', title: t('Late'), count: statusCounts.late, progress: getPercent(statusCounts.late), icon: ExclamationCircleOutlined, color: '#FF4D4F' },
                            { key: 'pending', title: t('Pending'), count: statusCounts.pending, progress: getPercent(statusCounts.pending), icon: ClockCircleOutlined, color: 'rgb(199, 200, 202)' },
                            { key: 'open', title: t('Open'), count: statusCounts.open, progress: getPercent(statusCounts.open), icon: ClockCircleOutlined, color: '#FAAD14' }
                        ].map((action, idx) => {
                            const Icon = action.icon;
                            return (
                                <Col xs={24} sm={12} md={4} key={idx} style={{ height: '100%' }}>
                                    <Card
                                        style={{
                                            height: '100%',
                                            minHeight: 100,
                                            width: "100%",
                                            alignItems: 'center',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                            <Icon style={{ fontSize: 28, color: action.color, marginRight: 12 }} />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <Text
                                                    style={{
                                                        marginBottom: 4,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {action.title}
                                                </Text>
                                                <Title
                                                    level={4}
                                                    style={{
                                                        marginTop: 0,
                                                        marginBottom: 4,
                                                        whiteSpace: 'normal',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {action.count}
                                                </Title>
                                                <Progress percent={action.progress} strokeColor={action.color} />
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                )}

                {tasks && (
                    <>
                        {Object.values(groupTasksByParent(tasks)).map((group, index) => {
                            const parent = group.parent;
                            const parentColor = getStatusColor(parent.status);
                            return (
                                <Row align="top" gutter={8} style={{ marginBottom: 8 }}>
                                    <Col flex="none">
                                        <div
                                            style={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: parentColor,
                                                color: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600,
                                                marginTop: 8,
                                            }}
                                        >
                                            {index + 1}
                                        </div>
                                    </Col>
                                    <Col flex="auto">
                                        <Card
                                            key={parent.id ?? index}
                                            bodyStyle={{ padding: 0 }}
                                            style={{ overflow: 'hidden', marginBottom: 0, cursor: 'pointer' }}
                                        >
                                            <div style={{ background: '#fff', padding: 12, borderLeft: `4px solid ${parentColor}` }}>
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <div
                                                            style={{
                                                                backgroundColor: parentColor,
                                                                color: '#fff',
                                                                fontWeight: 500,
                                                                borderRadius: 8,
                                                                padding: '2px 8px',
                                                                fontSize: 12,
                                                                display: 'inline-block',
                                                                marginRight: 8,
                                                            }}
                                                        >
                                                            {t(parent.status)}
                                                        </div>
                                                        <Text strong>
                                                            {t('Code')}: {parent.friendlyId}
                                                        </Text>
                                                    </Col>
                                                </Row>
                                                {parent.taskTypeId === 'df3cb0ad-9e0a-44e1-898c-3e3a5df1483e' ? (
                                                    <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                                        <Col span={3}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskChangeDate')}:</Text>
                                                            <br />
                                                            <Text strong>{moment(parent.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                        </Col>
                                                        <Col span={6} />
                                                        <Col span={6} />
                                                        <Col span={4}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskApproved')}:</Text>
                                                            <br />
                                                            <Text strong>{parent.userName}</Text>
                                                        </Col>
                                                        <Col span={4}>
                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Status')}:</Text>
                                                            <br />
                                                            {getTaskStatusTag(parent.status)}
                                                        </Col>
                                                    </Row>
                                                ) : (
                                                    <Link target="_blank" to={`/formbuilder/view-answer/${parent.id}`}>
                                                        <Tooltip title={t('Click to view the task')}>
                                                            <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                                                                <Col span={3}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskChangeDate')}:</Text>
                                                                    <br />
                                                                    <Text strong>{moment(parent.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                                </Col>
                                                                <Col span={6}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('ConfigPanel.Form.FormName')}:</Text>
                                                                    <br />
                                                                    <Text strong>{parent.name}</Text>
                                                                </Col>
                                                                <Col span={6}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}:</Text>
                                                                    <br />
                                                                    <Text strong>{parent.description}</Text>
                                                                </Col>
                                                                <Col span={4}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('AssignedTo')}:</Text>
                                                                    <br />
                                                                    <Text strong>{parent.userName}</Text>
                                                                </Col>
                                                                <Col span={4}>
                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Status')}:</Text>
                                                                    <br />
                                                                    {getTaskStatusTag(parent.status)}
                                                                </Col>
                                                            </Row>
                                                        </Tooltip>
                                                    </Link>
                                                )}
                                            </div>

                                            {group.approvals?.length > 0 && <>
                                                <Divider style={{ margin: 0 }} />
                                                <div style={{ padding: '8px 16px', background: "#fff" }}>
                                                    <Title level={5}>Fluxo de aprovação</Title>
                                                    {group.approvals.map((approval, idx) => {
                                                        const approvalColor = getStatusColor(approval.status);
                                                        return (
                                                            <Card
                                                                key={approval.id ?? idx}
                                                                bodyStyle={{ padding: 0 }}
                                                                style={{
                                                                    background: '#f9fafb',
                                                                    padding: 12,
                                                                    marginTop: 12,
                                                                    borderLeft: `4px solid ${approvalColor}`,
                                                                    cursor: 'pointer',
                                                                }}
                                                            >
                                                                {approval.taskTypeId === 'df3cb0ad-9e0a-44e1-898c-3e3a5df1483e' ? (
                                                                    <Row gutter={[8, 8]}>
                                                                        <Col span={3}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskChangeDate')}:</Text>
                                                                            <br />
                                                                            <Text strong>{moment(approval.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                                        </Col>
                                                                        <Col span={4}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskApproved')}:</Text>
                                                                            <br />
                                                                            <Text strong>{approval.userName}</Text>
                                                                        </Col>
                                                                        <Col span={4}>
                                                                            <Text type="secondary" style={{ fontSize: 12 }}>{t('Status')}:</Text>
                                                                            <br />
                                                                            {getTaskStatusTag(approval.status)}
                                                                        </Col>
                                                                        <Col span={6} />
                                                                        <Col span={6} />
                                                                    </Row>
                                                                ) : (
                                                                    <Link target="_blank" to={`/formbuilder/view-answer/${approval.id}`}>
                                                                        <Tooltip title={t('Click to view the approval task')}>
                                                                            <Row gutter={[8, 8]}>
                                                                                <Col span={3}>
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('TaskChangeDate')}:</Text>
                                                                                    <br />
                                                                                    <Text strong>{moment(approval.insertDate).format('YYYY-MMM-DD HH:mm')}</Text>
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Form Name')}:</Text>
                                                                                    <br />
                                                                                    <Text strong>{approval.name}</Text>
                                                                                </Col>
                                                                                <Col span={6}>
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Description')}:</Text>
                                                                                    <br />
                                                                                    <Text strong>{approval.description}</Text>
                                                                                </Col>
                                                                                <Col span={4}>
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('AssignedTo')}:</Text>
                                                                                    <br />
                                                                                    <Text strong>{approval.userName}</Text>
                                                                                </Col>
                                                                                <Col span={4}>
                                                                                    <Text type="secondary" style={{ fontSize: 12 }}>{t('Status')}:</Text>
                                                                                    <br />
                                                                                    {getTaskStatusTag(approval.status)}
                                                                                </Col>
                                                                            </Row>
                                                                        </Tooltip>
                                                                    </Link>
                                                                )}
                                                            </Card>
                                                        );
                                                    })}
                                                </div>
                                            </>}
                                        </Card>
                                    </Col>
                                </Row>
                            );
                        })}
                    </>
                )
                }

                {/* Lista de correlações (fluxos) */}
                {correlations && !tasks && (
                    <div style={{ height: "60vh", marginTop: 10 }}>
                        <AutoSizer>
                            {({ width, height }) => (
                                <List
                                    width={width}
                                    height={height}
                                    rowCount={filteredCorrelations.length}
                                    rowHeight={correlationCache.rowHeight}
                                    rowRenderer={renderCorrelationRow}
                                    deferredMeasurementCache={correlationCache}
                                />
                            )}
                        </AutoSizer>
                    </div>
                )}

                {/* Filtro da lista de workflows (local selecionado + filhos) */}
                {!correlations && !tasks && (
                    <Row gutter={[8, 8]} style={{ marginBottom: 10 }}>
                        <Col style={{ minWidth: 200 }}>
                            <TreeSelectLocationUser
                                placeholder={t("Location")}
                                includeDirectParents={false}
                                className="space-search"
                                value={workflowListLocationId != null && workflowListLocationId !== "" ? workflowListLocationId : undefined}
                                onChange={(e) => setWorkflowListLocationId(Array.isArray(e) ? (e[0] ?? null) : e ?? null)}
                                style={{ width: "100%" }}
                            />
                        </Col>
                        <Col style={{ minWidth: 200 }}>
                            <TreeSelectTopic
                                placeholder={t("Topic.Title")}
                                className="space-search"
                                value={workflowListTopicId != null && workflowListTopicId !== "" ? workflowListTopicId : undefined}
                                onChange={(e) => setWorkflowListTopicId(Array.isArray(e) ? (e[0] ?? null) : e ?? null)}
                                style={{ width: "100%" }}
                                loadAllTopics={true}
                            />
                        </Col>
                        <Col style={{ minWidth: 250 }}>
                            <DatePicker.RangePicker
                                value={workflowListDateRangeFilter && workflowListDateRangeFilter.length === 2 ? workflowListDateRangeFilter : null}
                                placeholder={[t("Reports.StartDate"), t("Reports.EndDate")]}
                                format="YYYY-MM-DD"
                                onChange={(dates) => {
                                    if (dates && dates.length === 2 && dates[0] && dates[1]) {
                                        setWorkflowListDateRangeFilter(dates);
                                    } else {
                                        setWorkflowListDateRangeFilter(null);
                                    }
                                }}
                                allowClear
                                style={{ width: '100%' }}
                            />
                        </Col>
                    </Row>
                )}

                {/* Lista de workflows (página inicial) */}
                {workflows && !correlations && !tasks && (
                    <div style={{ height: "80vh" }}>
                        <AutoSizer>
                            {({ width, height }) => (
                                <List
                                    width={width}
                                    height={height}
                                    rowCount={workflows.length}
                                    rowHeight={workflowCache.rowHeight}
                                    rowRenderer={renderWorkflowRow}
                                    deferredMeasurementCache={workflowCache}
                                />
                            )}
                        </AutoSizer>
                    </div>
                )}
            </div>
        </Spin >
    );
};

export default WorkflowTab;
